import { call, take, put, select, race, fork } from 'redux-saga/effects';
import { List } from 'immutable';
import { showToast, hideActiveToast } from './actions';
import { IToast } from '@/types/toast';
import { getActiveToast, getQueue } from './selectors';
import { TOASTR_HIDE_ACTIVE } from './constants';
import { LOCATION_CHANGE } from '@/App/router/constants';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const TIMEOUT = 5000;

function* watchLocationChange() {
  while (true) {
    yield take(LOCATION_CHANGE);
    const toast: IToast | undefined = yield select(getActiveToast);
    
    // Handle both Immutable Map and Plain Object access
    const persist = (toast as any)?.get ? (toast as any).get('persistOnLocationChange') : toast?.persistOnLocationChange;

    if (toast && persist) {
      yield delay(TIMEOUT);
      yield put(hideActiveToast());
    } else {
      yield put(hideActiveToast());
    }
  }
}

export function* watchToastrQueue() {
  // Fix: Fork OUTSIDE the loop to prevent creating thousands of watchers
  yield fork(watchLocationChange);

  while (true) {
    const queue: List<IToast> | IToast[] | undefined = yield select(getQueue);
    const activeToast: IToast | undefined = yield select(getActiveToast);

    // Defensive check: Handle Immutable List, Array, or undefined
    let queueSize = 0;
    if (queue) {
        if (List.isList(queue)) {
            // Explicit cast to fix TS error
            queueSize = (queue as List<IToast>).size;
        } else if (Array.isArray(queue)) {
            queueSize = queue.length;
        }
    }

    if (queueSize > 0) {
      // Get first item safely
      let toast;
      if (queue && List.isList(queue)) {
          toast = (queue as List<IToast>).get(0);
      } else if (queue && Array.isArray(queue)) {
          toast = queue[0];
      }

      if (toast) {
        yield put(showToast(toast));
        
        // Handle POJO or Immutable toast structure safely
        const toastData = (toast as any)?.toJS ? (toast as any).toJS() : toast;
        const currentTimeout = toastData.timeout || TIMEOUT; // {{ DYNAMIC TIMEOUT }}

        yield race({
          timeout: call(delay, currentTimeout), // {{ USE DYNAMIC TIMEOUT }}
          hide: take(TOASTR_HIDE_ACTIVE),
          reset: take('TOASTR_RESET'),
        });
      }
    } else {
      // Check active toast safely
      const action = (activeToast as any)?.get ? (activeToast as any).get('action') : activeToast?.action;
      
      if (!activeToast || !action) {
        yield put(hideActiveToast());
      }
      yield take(['TOASTR_QUEUE', 'TOASTR_RESET']);
    }
  }
}